/** WebSocket message handlers — routes incoming messages to the trading store. */

import type { ServerMessage } from './client'
import { useTradingStore } from '../state/trading'
import type { Position } from '../types/position'
import type { Order } from '../types/order'
import type { Alert } from '../types/alert'
import type { Fill } from '../types/fill'
import type { CashState, DrawdownMetrics, GreekBudget, RiskSnapshot } from '../types/risk'
import type {
  AutonomyReadiness,
  MarketDataReadinessSummary,
  ServiceStatus,
  SystemStatus,
  TradingMode,
} from '../types/system'

type Channel = 'positions' | 'orders' | 'risk' | 'alerts' | 'system' | 'market_data'
type AnyRecord = Record<string, unknown>
type PartialCashState = Partial<CashState>
type PartialDrawdownMetrics = Partial<DrawdownMetrics>

function asRecord(value: unknown): AnyRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as AnyRecord) : {}
}

function pick(data: AnyRecord, camelKey: string, snakeKey?: string): unknown {
  if (data[camelKey] !== undefined) return data[camelKey]
  if (snakeKey && data[snakeKey] !== undefined) return data[snakeKey]
  return undefined
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

function toString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function arrayPayload(data: unknown, key: string): unknown[] {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    const nested = (data as Record<string, unknown>)[key]
    if (Array.isArray(nested)) return nested
  }
  return []
}

function stringArrayPayload(data: unknown, key: string): string[] {
  return arrayPayload(data, key)
    .map((item) => (typeof item === 'string' ? item : null))
    .filter((item): item is string => Boolean(item))
}

function snapshotFailed(data: unknown): boolean {
  return Object.entries(asRecord(data)).some(
    ([key, value]) =>
      (key === 'error' || key.startsWith('error_')) &&
      typeof value === 'string' &&
      value.trim() !== '',
  )
}

function normalizePosition(raw: unknown): Position {
  const data = asRecord(raw)
  const greeks = asRecord(data.greeks)
  const isActive = data.is_active === false || data.isActive === false ? false : true
  const rawStatus = toString(pick(data, 'status')).toLowerCase()
  const settlementStatus = toString(pick(data, 'settlementStatus', 'settlement_status'))
  const normalizedStatus: Position['status'] | null =
    rawStatus === 'approaching_exit' ||
    rawStatus === 'at_risk' ||
    rawStatus === 'recently_closed' ||
    rawStatus === 'committed' ||
    rawStatus === 'active'
      ? rawStatus
      : null
  const status: Position['status'] =
    settlementStatus === 'committed'
      ? 'committed'
      : settlementStatus === 'reconciliation_required'
      ? 'reconciliation_required'
      : normalizedStatus ?? (isActive ? 'active' : 'recently_closed')

  return {
    id: toString(data.id),
    symbol: toString(data.symbol, 'UNKNOWN'),
    strategyType: toString(pick(data, 'strategyType', 'strategy_type'), 'unknown'),
    status,
    legs: arrayPayload(data.legs, 'legs').map(normalizePositionLeg),
    entryPrice: toNumber(pick(data, 'entryPrice', 'entry_price')),
    currentValue: toNumber(pick(data, 'currentValue', 'current_value')),
    unrealizedPnl: toNumber(pick(data, 'unrealizedPnl', 'unrealized_pnl')),
    maxLoss: toNumber(pick(data, 'maxLoss', 'max_loss')),
    collateral: toNumber(data.collateral),
    greeks: {
      delta: toNumber(greeks.delta),
      gamma: toNumber(greeks.gamma),
      theta: toNumber(greeks.theta),
      vega: toNumber(greeks.vega),
      rho: toNumber(greeks.rho),
    },
    dte: toNumber(data.dte),
    openedAt: toString(pick(data, 'openedAt', 'opened_at')),
    updatedAt: toString(pick(data, 'updatedAt', 'updated_at')),
    settlementStatus,
    reconciliationRequired: settlementStatus === 'reconciliation_required',
  }
}

function normalizePositionLeg(raw: unknown): Position['legs'][number] {
  const data = asRecord(raw)
  const optionType = toString(pick(data, 'optionType', 'option_type')) === 'call' ? 'call' : 'put'
  const side = toString(data.side) === 'buy' ? 'buy' : 'sell'
  return {
    optionType,
    side,
    strike: toNumber(data.strike),
    expiration: toString(data.expiration),
    quantity: toNumber(data.quantity),
    entryPrice: toNumber(pick(data, 'entryPrice', 'entry_price')),
    currentPrice: toNumber(pick(data, 'currentPrice', 'current_price')),
    iv: pick(data, 'iv') == null ? null : toNumber(data.iv),
    delta: toNumber(data.delta),
    gamma: toNumber(data.gamma),
    theta: toNumber(data.theta),
    vega: toNumber(data.vega),
  }
}

function normalizePositionPatch(raw: unknown): Partial<Position> {
  const data = asRecord(raw)
  const patch: Partial<Position> = {}
  if (data.symbol !== undefined) patch.symbol = toString(data.symbol)
  if (pick(data, 'strategyType', 'strategy_type') !== undefined) {
    patch.strategyType = toString(pick(data, 'strategyType', 'strategy_type'))
  }
  if (pick(data, 'entryPrice', 'entry_price') !== undefined) {
    patch.entryPrice = toNumber(pick(data, 'entryPrice', 'entry_price'))
  }
  if (pick(data, 'currentValue', 'current_value') !== undefined) {
    patch.currentValue = toNumber(pick(data, 'currentValue', 'current_value'))
  }
  if (pick(data, 'unrealizedPnl', 'unrealized_pnl') !== undefined) {
    patch.unrealizedPnl = toNumber(pick(data, 'unrealizedPnl', 'unrealized_pnl'))
  }
  if (pick(data, 'maxLoss', 'max_loss') !== undefined) {
    patch.maxLoss = toNumber(pick(data, 'maxLoss', 'max_loss'))
  }
  if (data.collateral !== undefined) patch.collateral = toNumber(data.collateral)
  if (data.greeks !== undefined) patch.greeks = normalizePosition(data).greeks
  if (data.dte !== undefined) patch.dte = toNumber(data.dte)
  if (pick(data, 'updatedAt', 'updated_at') !== undefined) {
    patch.updatedAt = toString(pick(data, 'updatedAt', 'updated_at'))
  }
  if (pick(data, 'settlementStatus', 'settlement_status') !== undefined) {
    patch.settlementStatus = toString(pick(data, 'settlementStatus', 'settlement_status'))
    patch.reconciliationRequired = patch.settlementStatus === 'reconciliation_required'
  }
  if (pick(data, 'openedAt', 'opened_at') !== undefined) {
    patch.openedAt = toString(pick(data, 'openedAt', 'opened_at'))
  }
  if (
    data.status !== undefined ||
    data.is_active !== undefined ||
    data.isActive !== undefined ||
    pick(data, 'settlementStatus', 'settlement_status') !== undefined
  ) {
    patch.status = normalizePosition(data).status
  }
  if (data.legs !== undefined)
    patch.legs = arrayPayload(data.legs, 'legs').map(normalizePositionLeg)
  return patch
}

function normalizeOrder(raw: unknown): Order {
  const data = asRecord(raw)
  const state = toString(data.state, 'unknown') as Order['state']
  return {
    id: toString(data.id),
    symbol: toString(data.symbol, 'UNKNOWN'),
    strategyType: toString(pick(data, 'strategyType', 'strategy_type'), 'unknown'),
    state,
    legs: arrayPayload(data.legs, 'legs').map(normalizeOrderLeg),
    createdAt: toString(pick(data, 'createdAt', 'created_at')),
    updatedAt: toString(pick(data, 'updatedAt', 'updated_at')),
    filledQuantity: toNumber(pick(data, 'filledQuantity', 'filled_quantity')),
    totalQuantity: toNumber(pick(data, 'totalQuantity', 'total_quantity')),
  }
}

function normalizeOrderLeg(raw: unknown): Order['legs'][number] {
  const data = asRecord(raw)
  return {
    optionType: toString(pick(data, 'optionType', 'option_type')) === 'call' ? 'call' : 'put',
    side: toString(data.side) === 'buy' ? 'buy' : 'sell',
    strike: toNumber(data.strike),
    expiration: toString(data.expiration),
    quantity: toNumber(data.quantity),
    limitPrice:
      pick(data, 'limitPrice', 'limit_price') == null
        ? null
        : toNumber(pick(data, 'limitPrice', 'limit_price')),
    fillPrice:
      pick(data, 'fillPrice', 'fill_price') == null
        ? null
        : toNumber(pick(data, 'fillPrice', 'fill_price')),
  }
}

function normalizeOrderPatch(raw: unknown): Partial<Order> {
  const data = asRecord(raw)
  const patch: Partial<Order> = {}
  if (data.symbol !== undefined) patch.symbol = toString(data.symbol)
  if (pick(data, 'strategyType', 'strategy_type') !== undefined) {
    patch.strategyType = toString(pick(data, 'strategyType', 'strategy_type'))
  }
  if (data.state !== undefined) patch.state = toString(data.state, 'unknown') as Order['state']
  if (data.legs !== undefined) patch.legs = arrayPayload(data.legs, 'legs').map(normalizeOrderLeg)
  if (pick(data, 'createdAt', 'created_at') !== undefined) {
    patch.createdAt = toString(pick(data, 'createdAt', 'created_at'))
  }
  if (pick(data, 'updatedAt', 'updated_at') !== undefined) {
    patch.updatedAt = toString(pick(data, 'updatedAt', 'updated_at'))
  }
  if (pick(data, 'filledQuantity', 'filled_quantity') !== undefined) {
    patch.filledQuantity = toNumber(pick(data, 'filledQuantity', 'filled_quantity'))
  }
  if (pick(data, 'totalQuantity', 'total_quantity') !== undefined) {
    patch.totalQuantity = toNumber(pick(data, 'totalQuantity', 'total_quantity'))
  }
  return patch
}

function normalizeAlert(raw: unknown): Alert {
  const data = asRecord(raw)
  return {
    id: toString(data.id),
    severity: toString(data.severity, 'info'),
    title: toString(data.title, 'Alert'),
    message: toString(data.message),
    source: toString(data.source),
    symbol: toNullableString(data.symbol),
    createdAt: toString(pick(data, 'createdAt', 'created_at')),
    acknowledgedAt: toNullableString(pick(data, 'acknowledgedAt', 'acknowledged_at')),
    resolvedAt: toNullableString(pick(data, 'resolvedAt', 'resolved_at')),
    snoozedUntil: toNullableString(pick(data, 'snoozedUntil', 'snoozed_until')),
    dismissedAt: toNullableString(pick(data, 'dismissedAt', 'dismissed_at')),
  }
}

function normalizeFill(raw: unknown): Fill {
  const data = asRecord(raw)
  return {
    id: toString(data.id),
    time: toString(data.time),
    symbol: toString(data.symbol, 'UNKNOWN'),
    action: toString(data.action),
    quantity: toNumber(data.quantity),
    fillPrice: toNumber(pick(data, 'fillPrice', 'fill_price') ?? data.price),
    limitPrice:
      pick(data, 'limitPrice', 'limit_price') == null
        ? null
        : toNumber(pick(data, 'limitPrice', 'limit_price')),
    slippage: pick(data, 'slippage') == null ? null : toNumber(data.slippage),
  }
}

function normalizeCash(raw: unknown): CashState {
  const data = asRecord(raw)
  return {
    totalCapital: toNumber(pick(data, 'totalCapital', 'total_capital')),
    settledCash: toNumber(pick(data, 'settledCash', 'settled_cash')),
    unsettledCash: toNumber(pick(data, 'unsettledCash', 'unsettled_cash')),
    committedCash: toNumber(pick(data, 'committedCash', 'committed_cash')),
    reservedCash: toNumber(pick(data, 'reservedCash', 'reserved_cash')),
    atRiskCash: toNumber(pick(data, 'atRiskCash', 'at_risk_cash')),
    availableCash: toNumber(pick(data, 'availableCash', 'available_cash')),
  }
}

function partialCash(raw: unknown): PartialCashState {
  const data = asRecord(raw)
  const patch: PartialCashState = {}
  const totalCapital = pick(data, 'totalCapital', 'total_capital')
  if (totalCapital !== undefined) patch.totalCapital = toNumber(totalCapital)
  const settledCash = pick(data, 'settledCash', 'settled_cash')
  if (settledCash !== undefined) patch.settledCash = toNumber(settledCash)
  const unsettledCash = pick(data, 'unsettledCash', 'unsettled_cash')
  if (unsettledCash !== undefined) patch.unsettledCash = toNumber(unsettledCash)
  const committedCash = pick(data, 'committedCash', 'committed_cash')
  if (committedCash !== undefined) patch.committedCash = toNumber(committedCash)
  const reservedCash = pick(data, 'reservedCash', 'reserved_cash')
  if (reservedCash !== undefined) patch.reservedCash = toNumber(reservedCash)
  const atRiskCash = pick(data, 'atRiskCash', 'at_risk_cash')
  if (atRiskCash !== undefined) patch.atRiskCash = toNumber(atRiskCash)
  const availableCash = pick(data, 'availableCash', 'available_cash')
  if (availableCash !== undefined) patch.availableCash = toNumber(availableCash)
  return patch
}

function normalizeBudget(raw: unknown, greek: string): GreekBudget {
  const data = asRecord(raw)
  return {
    greek: toString(data.greek, greek),
    current: toNumber(data.current),
    limit: toNumber(data.limit),
    unit: toString(data.unit, '$'),
  }
}

function mergeBudgetPatch(
  budgets: GreekBudget[],
  raw: unknown,
  greek: 'Delta' | 'Gamma' | 'Vega',
): GreekBudget[] {
  if (raw === undefined) return budgets

  const normalized = normalizeBudget(raw, greek)
  const target = greek.toLowerCase()
  const index = budgets.findIndex((budget) => budget.greek.toLowerCase() === target)
  if (index === -1) return [...budgets, normalized]

  return budgets.map((budget, budgetIndex) =>
    budgetIndex === index ? { ...budget, ...normalized, greek: budget.greek } : budget,
  )
}

function normalizeDrawdown(raw: unknown): DrawdownMetrics {
  const data = asRecord(raw)
  return {
    dailyPnl: toNumber(data.dailyPnl ?? data.dailyPct ?? data.daily_pct),
    weeklyPnl: toNumber(data.weeklyPnl ?? data.weeklyPct ?? data.weekly_pct),
    monthlyPnl: toNumber(data.monthlyPnl ?? data.monthlyPct ?? data.monthly_pct),
    peakToTrough: toNumber(data.peakToTrough ?? data.peakToTroughPct ?? data.peak_to_trough_pct),
    consecutiveLosses: toNumber(pick(data, 'consecutiveLosses', 'consecutive_losses')),
  }
}

function partialDrawdown(raw: unknown): PartialDrawdownMetrics {
  const data = asRecord(raw)
  const patch: PartialDrawdownMetrics = {}
  const dailyPnl = data.dailyPnl ?? data.dailyPct ?? data.daily_pct
  if (dailyPnl !== undefined) patch.dailyPnl = toNumber(dailyPnl)
  const weeklyPnl = data.weeklyPnl ?? data.weeklyPct ?? data.weekly_pct
  if (weeklyPnl !== undefined) patch.weeklyPnl = toNumber(weeklyPnl)
  const monthlyPnl = data.monthlyPnl ?? data.monthlyPct ?? data.monthly_pct
  if (monthlyPnl !== undefined) patch.monthlyPnl = toNumber(monthlyPnl)
  const peakToTrough = data.peakToTrough ?? data.peakToTroughPct ?? data.peak_to_trough_pct
  if (peakToTrough !== undefined) patch.peakToTrough = toNumber(peakToTrough)
  const consecutiveLosses = pick(data, 'consecutiveLosses', 'consecutive_losses')
  if (consecutiveLosses !== undefined) patch.consecutiveLosses = toNumber(consecutiveLosses)
  return patch
}

function normalizeRisk(raw: unknown): RiskSnapshot {
  const data = asRecord(raw)
  const greeks = asRecord(pick(data, 'portfolioGreeks', 'portfolio_greeks'))
  const budgets = Array.isArray(data.greekBudgets)
    ? data.greekBudgets.map((budget, index) => normalizeBudget(budget, `budget-${index}`))
    : [
        normalizeBudget(pick(data, 'deltaBudget', 'delta_budget'), 'delta'),
        normalizeBudget(pick(data, 'gammaBudget', 'gamma_budget'), 'gamma'),
        normalizeBudget(pick(data, 'vegaBudget', 'vega_budget'), 'vega'),
      ].filter((budget) => budget.limit > 0 || budget.current !== 0)

  return {
    cashState: normalizeCash(pick(data, 'cashState', 'cash_state') ?? data.cash),
    greekBudgets: budgets,
    drawdown: normalizeDrawdown(data.drawdown),
    portfolioDelta: toNumber(
      pick(data, 'portfolioDelta', 'portfolio_delta'),
      toNumber(greeks.delta),
    ),
    portfolioGamma: toNumber(
      pick(data, 'portfolioGamma', 'portfolio_gamma'),
      toNumber(greeks.gamma),
    ),
    portfolioTheta: toNumber(
      pick(data, 'portfolioTheta', 'portfolio_theta'),
      toNumber(greeks.theta),
    ),
    portfolioVega: toNumber(pick(data, 'portfolioVega', 'portfolio_vega'), toNumber(greeks.vega)),
    portfolioRho: toNumber(pick(data, 'portfolioRho', 'portfolio_rho'), toNumber(greeks.rho)),
    var95: pick(data, 'var95', 'var_95') == null ? null : toNumber(pick(data, 'var95', 'var_95')),
    var99: pick(data, 'var99', 'var_99') == null ? null : toNumber(pick(data, 'var99', 'var_99')),
    var5day95:
      pick(data, 'var5day95', 'var_5day_95') == null
        ? null
        : toNumber(pick(data, 'var5day95', 'var_5day_95')),
    maxPortfolioLoss: toNumber(pick(data, 'maxPortfolioLoss', 'max_portfolio_loss')),
  }
}

function normalizeService(name: string, raw: unknown): ServiceStatus {
  const data = asRecord(raw)
  return {
    name: toString(data.name, name),
    status: toString(data.status ?? raw, 'unknown') as ServiceStatus['status'],
    latencyMs:
      pick(data, 'latencyMs', 'latency_ms') == null
        ? null
        : toNumber(pick(data, 'latencyMs', 'latency_ms')),
    uptimePct:
      pick(data, 'uptimePct', 'uptime_pct') == null
        ? null
        : toNumber(pick(data, 'uptimePct', 'uptime_pct')),
  }
}

function normalizeServices(raw: unknown): ServiceStatus[] {
  if (Array.isArray(raw)) {
    return raw.map((service, index) => normalizeService(`service-${index}`, service))
  }
  const data = asRecord(raw)
  return Object.entries(data).map(([name, status]) => normalizeService(name, status))
}

function normalizeSystem(raw: unknown): SystemStatus {
  const data = asRecord(raw)
  const tradingMode = pick(data, 'tradingMode', 'trading_mode')
  const autonomyReadiness = pick(data, 'autonomyReadiness', 'autonomy_readiness')
  const marketDataReadiness = pick(data, 'marketDataReadiness', 'market_data_readiness')
  return {
    overall: toString(data.overall ?? data.status, 'unknown'),
    tradingMode: toString(tradingMode, 'unknown') as TradingMode,
    uptimeSeconds: toNumber(pick(data, 'uptimeSeconds', 'uptime_seconds')),
    services: normalizeServices(data.services),
    blockers: stringArrayPayload(data, 'blockers'),
    broker: {
      connected: Boolean(asRecord(data.broker).connected),
      port: toNumber(asRecord(data.broker).port),
      lastHeartbeat: toNullableString(
        pick(asRecord(data.broker), 'lastHeartbeat', 'last_heartbeat'),
      ),
      pingLatencyMs:
        pick(asRecord(data.broker), 'pingLatencyMs', 'ping_latency_ms') == null
          ? null
          : toNumber(pick(asRecord(data.broker), 'pingLatencyMs', 'ping_latency_ms')),
      reconnectCountToday: toNumber(
        pick(asRecord(data.broker), 'reconnectCountToday', 'reconnect_count_today'),
      ),
    },
    autonomyReadiness:
      autonomyReadiness && typeof autonomyReadiness === 'object'
        ? (autonomyReadiness as AutonomyReadiness)
        : undefined,
    marketDataReadiness:
      marketDataReadiness && typeof marketDataReadiness === 'object'
        ? (marketDataReadiness as MarketDataReadinessSummary)
        : undefined,
  }
}

function mergeRiskUpdate(current: RiskSnapshot | null, raw: unknown): RiskSnapshot {
  if (!current) return normalizeRisk(raw)

  const data = asRecord(raw)
  const greeks = asRecord(pick(data, 'portfolioGreeks', 'portfolio_greeks'))
  const next: RiskSnapshot = { ...current }

  const cashRaw = pick(data, 'cashState', 'cash_state') ?? data.cash
  if (cashRaw !== undefined) {
    next.cashState = { ...current.cashState, ...partialCash(cashRaw) }
  }

  if (data.greekBudgets !== undefined) {
    next.greekBudgets = normalizeRisk(data).greekBudgets
  } else {
    next.greekBudgets = mergeBudgetPatch(
      next.greekBudgets,
      pick(data, 'deltaBudget', 'delta_budget'),
      'Delta',
    )
    next.greekBudgets = mergeBudgetPatch(
      next.greekBudgets,
      pick(data, 'gammaBudget', 'gamma_budget'),
      'Gamma',
    )
    next.greekBudgets = mergeBudgetPatch(
      next.greekBudgets,
      pick(data, 'vegaBudget', 'vega_budget'),
      'Vega',
    )
  }

  if (data.drawdown !== undefined) {
    next.drawdown = { ...current.drawdown, ...partialDrawdown(data.drawdown) }
  }

  const portfolioDelta = pick(data, 'portfolioDelta', 'portfolio_delta') ?? greeks.delta
  if (portfolioDelta !== undefined) next.portfolioDelta = toNumber(portfolioDelta)
  const portfolioGamma = pick(data, 'portfolioGamma', 'portfolio_gamma') ?? greeks.gamma
  if (portfolioGamma !== undefined) next.portfolioGamma = toNumber(portfolioGamma)
  const portfolioTheta = pick(data, 'portfolioTheta', 'portfolio_theta') ?? greeks.theta
  if (portfolioTheta !== undefined) next.portfolioTheta = toNumber(portfolioTheta)
  const portfolioVega = pick(data, 'portfolioVega', 'portfolio_vega') ?? greeks.vega
  if (portfolioVega !== undefined) next.portfolioVega = toNumber(portfolioVega)
  const portfolioRho = pick(data, 'portfolioRho', 'portfolio_rho') ?? greeks.rho
  if (portfolioRho !== undefined) next.portfolioRho = toNumber(portfolioRho)

  const var95 = pick(data, 'var95', 'var_95')
  if (var95 !== undefined) next.var95 = var95 == null ? null : toNumber(var95)
  const var99 = pick(data, 'var99', 'var_99')
  if (var99 !== undefined) next.var99 = var99 == null ? null : toNumber(var99)
  const var5day95 = pick(data, 'var5day95', 'var_5day_95')
  if (var5day95 !== undefined) next.var5day95 = var5day95 == null ? null : toNumber(var5day95)
  const maxPortfolioLoss = pick(data, 'maxPortfolioLoss', 'max_portfolio_loss')
  if (maxPortfolioLoss !== undefined) next.maxPortfolioLoss = toNumber(maxPortfolioLoss)

  return next
}

function mergeSystemUpdate(current: SystemStatus | null, raw: unknown): SystemStatus {
  if (!current) return normalizeSystem(raw)

  const data = asRecord(raw)
  const broker = asRecord(data.broker)
  const next: SystemStatus = { ...current }

  const overall = data.overall ?? data.status
  if (overall !== undefined) next.overall = toString(overall, current.overall)
  const tradingMode = pick(data, 'tradingMode', 'trading_mode')
  if (tradingMode !== undefined)
    next.tradingMode = toString(tradingMode, current.tradingMode) as TradingMode
  const uptimeSeconds = pick(data, 'uptimeSeconds', 'uptime_seconds')
  if (uptimeSeconds !== undefined)
    next.uptimeSeconds = toNumber(uptimeSeconds, current.uptimeSeconds)
  if (data.services !== undefined) next.services = normalizeServices(data.services)
  if (data.blockers !== undefined) {
    next.blockers = Array.from(
      new Set([...(current.blockers ?? []), ...stringArrayPayload(data, 'blockers')]),
    )
  }
  const autonomyReadiness = pick(data, 'autonomyReadiness', 'autonomy_readiness')
  if (autonomyReadiness && typeof autonomyReadiness === 'object') {
    next.autonomyReadiness = autonomyReadiness as AutonomyReadiness
  }
  const marketDataReadiness = pick(data, 'marketDataReadiness', 'market_data_readiness')
  if (marketDataReadiness && typeof marketDataReadiness === 'object') {
    next.marketDataReadiness = marketDataReadiness as MarketDataReadinessSummary
  }

  if (data.broker !== undefined) {
    next.broker = { ...current.broker }
    if (broker.connected !== undefined) next.broker.connected = Boolean(broker.connected)
    if (broker.port !== undefined) next.broker.port = toNumber(broker.port, current.broker.port)
    const lastHeartbeat = pick(broker, 'lastHeartbeat', 'last_heartbeat')
    if (lastHeartbeat !== undefined) next.broker.lastHeartbeat = toNullableString(lastHeartbeat)
    const pingLatencyMs = pick(broker, 'pingLatencyMs', 'ping_latency_ms')
    if (pingLatencyMs !== undefined) {
      next.broker.pingLatencyMs = pingLatencyMs == null ? null : toNumber(pingLatencyMs)
    }
    const reconnectCountToday = pick(broker, 'reconnectCountToday', 'reconnect_count_today')
    if (reconnectCountToday !== undefined) {
      next.broker.reconnectCountToday = toNumber(
        reconnectCountToday,
        current.broker.reconnectCountToday,
      )
    }
  }

  return next
}

/**
 * Dispatch a snapshot message — replaces the entire store slice for that channel.
 */
function handleSnapshot(channel: Channel, data: unknown): void {
  const store = useTradingStore.getState()
  if (snapshotFailed(data)) return

  switch (channel) {
    case 'positions':
      store.setPositions(arrayPayload(data, 'positions').map(normalizePosition))
      break
    case 'orders':
      store.setOrders(arrayPayload(data, 'orders').map(normalizeOrder))
      break
    case 'alerts':
      store.setAlerts(arrayPayload(data, 'alerts').map(normalizeAlert))
      break
    case 'risk':
      store.setRisk(normalizeRisk(data))
      break
    case 'system':
      if (!('autonomyReadiness' in asRecord(data)) && !('marketDataReadiness' in asRecord(data))) {
        return
      }
      store.setSystem(mergeSystemUpdate(store.system, data))
      break
    case 'market_data':
      // Market data doesn't have a dedicated store slice yet
      break
  }
}

/**
 * Dispatch an update (delta) message — merges into the existing store state.
 */
function handleUpdate(channel: Channel, data: unknown): void {
  const store = useTradingStore.getState()
  const payload = data as Record<string, unknown>
  if (payload.heartbeat === true) return

  switch (channel) {
    case 'positions': {
      const action = payload.action as string | undefined
      if (action === 'add') {
        // New position — add to list
        const positions = store.positions.concat(normalizePosition(payload.position))
        store.setPositions(positions)
      } else if (action === 'remove') {
        store.removePosition(payload.id as string)
      } else {
        // Default: partial update
        store.updatePosition(payload.id as string, normalizePositionPatch(payload))
      }
      break
    }
    case 'orders': {
      const action = payload.action as string | undefined
      if (action === 'add') {
        store.addOrder(normalizeOrder(payload.order))
      } else if (action === 'fill') {
        store.addFill(normalizeFill(payload.fill))
      } else {
        store.updateOrder(payload.id as string, normalizeOrderPatch(payload))
      }
      break
    }
    case 'alerts': {
      const action = payload.action as string | undefined
      const id = (payload.id ?? payload.alert_id) as string | undefined
      if (action === 'add') {
        store.addAlert(normalizeAlert(payload.alert))
      } else if (action === 'acknowledge' || action === 'acknowledged') {
        if (id) store.acknowledgeAlert(id)
      } else if (action === 'resolve' || action === 'resolved') {
        if (id) store.resolveAlert(id)
      } else if (action === 'dismiss' || action === 'dismissed') {
        if (id) {
          store.setAlerts(
            store.alerts.map((alert) =>
              alert.id === id ? { ...alert, dismissedAt: new Date().toISOString() } : alert,
            ),
          )
        }
      } else if (action === 'silence' || action === 'silenced') {
        if (id) {
          const snoozedUntil =
            (payload.snoozedUntil as string | undefined) ??
            (payload.silenced_until as string | undefined)
          store.setAlerts(
            store.alerts.map((alert) =>
              alert.id === id
                ? { ...alert, snoozedUntil: snoozedUntil ?? alert.snoozedUntil }
                : alert,
            ),
          )
        }
      }
      break
    }
    case 'risk':
      store.setRisk(mergeRiskUpdate(store.risk, data))
      break
    case 'system':
      store.setSystem(mergeSystemUpdate(store.system, data))
      break
    case 'market_data':
      break
  }
}

/**
 * Main message router — call from WsClient's message handler.
 */
export function routeMessage(msg: ServerMessage): void {
  if (!msg.channel) return

  if (msg.type === 'snapshot') {
    handleSnapshot(msg.channel as Channel, msg.data)
  } else if (msg.type === 'update') {
    handleUpdate(msg.channel as Channel, msg.data)
  }
}
